import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useApi } from '../lib/api'
import { useCurrentUser } from '../lib/auth'
import type { Order, OrderItem, StatusHistoryEntry, Invoice, OrderStatus, Flag } from '../types'
import { STATUS_LABELS, NEXT_STATUSES, INVOICE_STATUS_LABELS, FLAG_MATERIALS } from '../types'
import { FlagThumbnail } from '../components/FlagThumbnail'
import { FlagPicker } from '../components/FlagPicker'
import { PdfThumbnail } from '../components/PdfPreview'
import { OrderStateMachine } from '../components/StateMachineVisualizer'

interface OrderDetailData {
  order: Order
  items: OrderItem[]
  history: StatusHistoryEntry[]
  invoices: Invoice[]
}

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { apiFetch } = useApi()
  const { user } = useCurrentUser()
  const [data, setData] = useState<OrderDetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Status change state
  const [statusSaving, setStatusSaving] = useState(false)
  const [modal, setModal] = useState<'none' | 'cancel_order' | 'cancel_invoice'>('none')
  const [modalNote, setModalNote] = useState('')
  const [cancelInvoiceId, setCancelInvoiceId] = useState<number | null>(null)

  // Invoice sending state
  const [sendingInvoice, setSendingInvoice] = useState(false)

  // Item add state
  const [itemForm, setItemForm] = useState({ quantity: 1, width_cm: 100, height_cm: 150, material: 'Polyester' })
  const [itemFlag, setItemFlag] = useState<Flag | null>(null)
  const [addingItem, setAddingItem] = useState(false)
  const [deletingItem, setDeletingItem] = useState<number | null>(null)

  function reload() {
    setLoading(true)
    apiFetch<OrderDetailData>(`/api/orders/${id}`)
      .then((d) => setData(d))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { reload() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleStatusTransition(status: OrderStatus, note: string) {
    setStatusSaving(true)
    try {
      await apiFetch(`/api/orders/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status, notes: note || undefined }),
      })
      setModal('none')
      setModalNote('')
      reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Fehler beim Aktualisieren des Status')
    } finally {
      setStatusSaving(false)
    }
  }

  async function handleSendInvoice(note?: string) {
    setSendingInvoice(true)
    try {
      await apiFetch<Invoice>(`/api/invoices`, {
        method: 'POST',
        body: JSON.stringify({ order_id: Number(id), notes: note || undefined }),
      })
      setModal('none')
      setModalNote('')
      reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Fehler beim Senden der Rechnung')
    } finally {
      setSendingInvoice(false)
    }
  }

  async function handleInvoiceStatusUpdate(invoiceId: number, status: 'paid' | 'cancelled', note?: string) {
    try {
      await apiFetch<Invoice>(`/api/invoices/${invoiceId}`, {
        method: 'PUT',
        body: JSON.stringify({ status, notes: note || undefined }),
      })
      setModal('none')
      setModalNote('')
      setCancelInvoiceId(null)
      reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Fehler beim Aktualisieren der Rechnung')
    }
  }

  async function handleAddItem(e: React.FormEvent) {
    e.preventDefault()
    if (!itemFlag) return
    setAddingItem(true)
    try {
      const item = await apiFetch<OrderItem>(`/api/orders/${id}/items`, {
        method: 'POST',
        body: JSON.stringify({ ...itemForm, flag_id: itemFlag.id }),
      })
      setData((d) => d ? { ...d, items: [...d.items, item] } : d)
      setItemForm({ quantity: 1, width_cm: 100, height_cm: 150, material: 'Polyester' })
      setItemFlag(null)
    } finally {
      setAddingItem(false)
    }
  }

  async function handleDeleteItem(itemId: number) {
    setDeletingItem(itemId)
    try {
      await apiFetch(`/api/orders/${id}/items/${itemId}`, { method: 'DELETE' })
      setData((d) => d ? { ...d, items: d.items.filter((i) => i.id !== itemId) } : d)
    } finally {
      setDeletingItem(null)
    }
  }

  if (loading) return <div className="loading">Auftrag wird geladen…</div>
  if (error) return <div className="error-msg">{error}</div>
  if (!data) return null

  const { order, items, history, invoices } = data
  const canChangeStatus = user?.roles.some((r) => ['admin', 'sales', 'fulfillment', 'design', 'accounting'].includes(r))
  const canManageInvoice = user?.roles.some((r) => ['admin', 'accounting'].includes(r))

  // Latest active (non-cancelled) sent invoice
  const latestInvoice = invoices.find((i) => i.status !== 'cancelled') ?? null

  function fmtEuro(n: number) {
    return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/orders" style={{ color: 'var(--color-muted)', fontSize: 13 }}>← Aufträge</Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
            <h1 style={{ margin: 0 }}>{order.reference}</h1>
            <span className={`badge badge-${order.status}`} style={{ fontSize: 14 }}>
              {STATUS_LABELS[order.status]}
            </span>
          </div>
        </div>
      </div>

      <OrderStateMachine currentStatus={order.status} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Order info */}
          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Auftragsdetails</h2>
            <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px 12px' }}>
              <dt style={{ color: 'var(--color-muted)', fontSize: 13 }}>Kunde</dt>
              <dd>{order.client_name}</dd>
              <dt style={{ color: 'var(--color-muted)', fontSize: 13 }}>E-Mail</dt>
              <dd><a href={`mailto:${order.client_email}`}>{order.client_email}</a></dd>
              <dt style={{ color: 'var(--color-muted)', fontSize: 13 }}>Erstellt</dt>
              <dd>{new Date(order.created_at).toLocaleString('de-DE')}</dd>
              <dt style={{ color: 'var(--color-muted)', fontSize: 13 }}>Aktualisiert</dt>
              <dd>{new Date(order.updated_at).toLocaleString('de-DE')}</dd>
            </dl>
            {order.body && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
                <div style={{ color: 'var(--color-muted)', fontSize: 12, marginBottom: 6 }}>Originaler E-Mail-Inhalt</div>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, fontFamily: 'inherit' }}>{order.body}</pre>
              </div>
            )}
          </div>

          {/* Line items */}
          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Positionen</h2>

            {items.length === 0 ? (
              <p style={{ color: 'var(--color-muted)', marginBottom: 16 }}>Noch keine Positionen hinzugefügt.</p>
            ) : (
              <table style={{ marginBottom: 16 }}>
                <thead>
                  <tr>
                    <th>Flagge</th>
                    <th>Anzahl</th>
                    <th>Größe (cm)</th>
                    <th>Material</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {item.flag_type && (
                            <FlagThumbnail
                              id={item.flag_id}
                              type={item.flag_type}
                              imageUrl={item.flag_image_url}
                              name={item.flag_name ?? ''}
                              width={40}
                              height={25}
                            />
                          )}
                          <span style={{ fontSize: 13 }}>
                            {item.flag_name ?? '—'}
                            {item.flag_type === 'custom' && (
                              <span style={{ marginLeft: 5, fontSize: 11, color: 'var(--color-muted)', fontStyle: 'italic' }}>Eigene</span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td>{item.quantity}×</td>
                      <td>{item.width_cm} × {item.height_cm}</td>
                      <td>{item.material}</td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '2px 8px', fontSize: 12, color: 'var(--color-danger, #ef4444)' }}
                          disabled={deletingItem === item.id}
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          {deletingItem === item.id ? '…' : '✕'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Add position form */}
            <form onSubmit={handleAddItem}>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 4 }}>Flagge *</div>
                <FlagPicker value={itemFlag} onChange={setItemFlag} />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 4 }}>Anzahl</div>
                  <input
                    type="number"
                    min={1}
                    value={itemForm.quantity}
                    onChange={(e) => setItemForm((f) => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
                    style={{ width: 70 }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 4 }}>Breite (cm)</div>
                  <input
                    type="number"
                    min={1}
                    value={itemForm.width_cm}
                    onChange={(e) => setItemForm((f) => ({ ...f, width_cm: parseInt(e.target.value) || 0 }))}
                    style={{ width: 90 }}
                  />
                </div>
                <div style={{ alignSelf: 'flex-end', paddingBottom: 8, color: 'var(--color-muted)' }}>×</div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 4 }}>Höhe (cm)</div>
                  <input
                    type="number"
                    min={1}
                    value={itemForm.height_cm}
                    onChange={(e) => setItemForm((f) => ({ ...f, height_cm: parseInt(e.target.value) || 0 }))}
                    style={{ width: 90 }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 4 }}>Material</div>
                  <select
                    value={itemForm.material}
                    onChange={(e) => setItemForm((f) => ({ ...f, material: e.target.value }))}
                    style={{ width: 140 }}
                  >
                    {FLAG_MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <button
                  type="submit"
                  className="btn btn-secondary"
                  disabled={addingItem || !itemForm.width_cm || !itemForm.height_cm || !itemFlag}
                >
                  {addingItem ? '…' : '+ Hinzufügen'}
                </button>
              </div>
            </form>
          </div>

          {/* Production sheet PDF */}
          <div className="card">
            <div style={{ marginBottom: 14 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Produktionsauftrag (PDF)</h2>
              <p style={{ fontSize: 12, color: 'var(--color-muted)', margin: '4px 0 0' }}>Internes Dokument für die Produktion – mit QR-Code, ohne Preisangaben. Klicken Sie auf die Vorschau, um das PDF zu öffnen.</p>
            </div>
            {order.production_pdf_r2_key ? (
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <PdfThumbnail
                  src={`/api/orders/${id}/production-pdf`}
                  label={`Produktionsauftrag ${order.reference}`}
                  width={120}
                />
                <div style={{ fontSize: 13, color: 'var(--color-muted)', lineHeight: 1.6 }}>
                  <p style={{ margin: 0 }}>Wird automatisch aktualisiert, wenn Positionen geändert werden.</p>
                  <p style={{ margin: '8px 0 0' }}>
                    Verwenden Sie <strong>„Produktionsauftrag drucken"</strong> im rechten Bereich, um das Dokument zu öffnen und auszudrucken.
                  </p>
                </div>
              </div>
            ) : (
              <p style={{ color: 'var(--color-muted)', fontSize: 13, margin: 0 }}>
                Noch kein PDF — fügen Sie Positionen hinzu, um den Produktionsauftrag zu generieren.
              </p>
            )}
          </div>

          {/* Invoice PDF */}
          <div className="card">
            <div style={{ marginBottom: 14 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Rechnung (PDF)</h2>
              <p style={{ fontSize: 12, color: 'var(--color-muted)', margin: '4px 0 0' }}>Klicken Sie auf die Vorschau, um das PDF in voller Größe zu sehen.</p>
            </div>
            {order.pdf_r2_key ? (
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <PdfThumbnail
                  src={`/api/orders/${id}/pdf`}
                  label={`Rechnung ${order.reference}`}
                  width={120}
                />
                <div style={{ fontSize: 13, color: 'var(--color-muted)', lineHeight: 1.6 }}>
                  <p style={{ margin: 0 }}>Diese Vorschau wird automatisch aktualisiert, sobald Positionen hinzugefügt oder geändert werden.</p>
                  {canManageInvoice && (
                    <p style={{ margin: '8px 0 0' }}>
                      Verwenden Sie <strong>„Rechnung senden"</strong> im rechten Bereich, um eine offizielle Rechnung zu erstellen und im Rechnungsverlauf zu speichern.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p style={{ color: 'var(--color-muted)', fontSize: 13, margin: 0 }}>
                Noch kein PDF — fügen Sie Positionen hinzu, um die Rechnung zu generieren.
              </p>
            )}
          </div>

          {/* Sent invoices history */}
          {invoices.length > 0 && (
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Rechnungsverlauf</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {invoices.map((inv) => (
                  <div
                    key={inv.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '10px 12px',
                      border: '1px solid var(--color-border)',
                      borderRadius: 6,
                      background: inv.status === 'cancelled' ? 'var(--color-bg, #f9fafb)' : 'white',
                    }}
                  >
                    {/* Thumbnail */}
                    {inv.r2_key && (
                      <PdfThumbnail
                        src={`/api/invoices/${inv.id}/pdf`}
                        label={inv.invoice_number}
                        width={44}
                      />
                    )}

                    {/* Details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{inv.invoice_number}</div>
                      <div style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 2 }}>
                        Gesendet: {new Date(inv.sent_at).toLocaleDateString('de-DE')}
                        {' · '}
                        {fmtEuro(inv.total_amount)}
                      </div>
                    </div>

                    <span className={`badge badge-${inv.status}`} style={{ flexShrink: 0 }}>
                      {INVOICE_STATUS_LABELS[inv.status]}
                    </span>

                    {/* Actions */}
                    {canManageInvoice && inv.status === 'shipped' && (
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button
                          className="btn btn-primary"
                          style={{ padding: '3px 10px', fontSize: 12 }}
                          onClick={() => handleInvoiceStatusUpdate(inv.id, 'paid')}
                        >
                          Bezahlt
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '3px 10px', fontSize: 12 }}
                          onClick={() => { setCancelInvoiceId(inv.id); setModal('cancel_invoice') }}
                        >
                          Stornieren
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Change status */}
          {canChangeStatus && order.status !== 'completed' && order.status !== 'cancelled' && (
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Nächster Status</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(NEXT_STATUSES[order.status] ?? []).filter((s) => s !== 'cancelled').map((s) => (
                  <button
                    key={s}
                    className="btn btn-primary"
                    disabled={statusSaving}
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => handleStatusTransition(s, '')}
                  >
                    {STATUS_LABELS[s]}
                  </button>
                ))}
                <button
                  className="btn btn-danger"
                  disabled={statusSaving}
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => setModal('cancel_order')}
                >
                  Abgebrochen
                </button>
              </div>
            </div>
          )}

          {/* Print production sheet */}
          {order.status !== 'cancelled' && (
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Produktionsauftrag drucken</h2>
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 12 }}>
                Öffnet den internen Produktionsauftrag als PDF – mit QR-Code und Positionen, ohne Preisangaben.
              </p>
              {!order.production_pdf_r2_key ? (
                <p style={{ fontSize: 12, color: 'var(--color-muted)' }}>
                  Fügen Sie zuerst Positionen hinzu.
                </p>
              ) : (
                <button
                  className="btn btn-secondary"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => window.open(`/api/orders/${id}/production-pdf`, '_blank')}
                >
                  🖨️ Produktionsauftrag drucken
                </button>
              )}
            </div>
          )}

          {/* Send invoice */}
          {canManageInvoice && order.status !== 'cancelled' && (
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Rechnung senden</h2>
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 12 }}>
                Erstellt eine offizielle Rechnung und speichert sie im Rechnungsverlauf.
              </p>
              {items.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--color-muted)' }}>
                  Fügen Sie zuerst Positionen hinzu.
                </p>
              ) : (
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', justifyContent: 'center' }}
                  disabled={sendingInvoice}
                  onClick={() => handleSendInvoice()}
                >
                  {sendingInvoice ? 'Wird erstellt…' : '📄 Rechnung senden'}
                </button>
              )}
              {latestInvoice && (
                <p style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 8, margin: '8px 0 0' }}>
                  Zuletzt gesendet: <strong>{latestInvoice.invoice_number}</strong>
                  {' · '}
                  <span className={`badge badge-${latestInvoice.status}`} style={{ fontSize: 11 }}>
                    {INVOICE_STATUS_LABELS[latestInvoice.status]}
                  </span>
                </p>
              )}
            </div>
          )}

          {/* Status history */}
          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Verlauf</h2>
            {history.length === 0 ? (
              <p style={{ color: 'var(--color-muted)' }}>Noch kein Verlauf.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {history.map((h) => (
                  <div key={h.id} style={{ borderLeft: '3px solid var(--color-border)', paddingLeft: 12 }}>
                    <span className={`badge badge-${h.status}`}>{STATUS_LABELS[h.status]}</span>
                    {h.notes && <p style={{ fontSize: 13, marginTop: 4 }}>{h.notes}</p>}
                    <p style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 4 }}>
                      {h.changed_by_name ?? 'System'} · {new Date(h.changed_at).toLocaleString('de-DE')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {modal !== 'none' && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) { setModal('none'); setModalNote('') } }}
        >
          <div className="card" style={{ width: 440, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>

            {modal === 'cancel_order' && (
              <>
                <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Auftrag abbrechen</h2>
                <p style={{ fontSize: 13, color: 'var(--color-muted)', marginBottom: 12 }}>
                  Bitte geben Sie einen Grund für die Absage an.
                </p>
                <textarea
                  rows={4}
                  style={{ width: '100%', marginBottom: 12 }}
                  value={modalNote}
                  onChange={(e) => setModalNote(e.target.value)}
                  placeholder="Grund für Absage…"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-danger"
                    disabled={!modalNote.trim() || statusSaving}
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => handleStatusTransition('cancelled', modalNote)}
                  >
                    {statusSaving ? 'Wird gespeichert…' : 'Bestätigen'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => { setModal('none'); setModalNote('') }}
                  >
                    Abbrechen
                  </button>
                </div>
              </>
            )}

            {modal === 'cancel_invoice' && (
              <>
                <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Rechnung stornieren</h2>
                <p style={{ fontSize: 13, color: 'var(--color-muted)', marginBottom: 12 }}>
                  Bitte geben Sie einen Stornierungsgrund an.
                </p>
                <textarea
                  rows={4}
                  style={{ width: '100%', marginBottom: 12 }}
                  value={modalNote}
                  onChange={(e) => setModalNote(e.target.value)}
                  placeholder="Stornierungsgrund…"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-danger"
                    disabled={!modalNote.trim() || cancelInvoiceId === null}
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => {
                      if (cancelInvoiceId !== null) {
                        handleInvoiceStatusUpdate(cancelInvoiceId, 'cancelled', modalNote)
                      }
                    }}
                  >
                    Stornieren
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => { setModal('none'); setModalNote(''); setCancelInvoiceId(null) }}
                  >
                    Abbrechen
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

    </div>
  )
}
